import { computed, inject, ref } from 'vue';
import { useStore } from 'vuex';
import { mapActions, mapGetters } from '@/store/lib/vue3helpers.js';
import { differenceInSeconds } from 'date-fns';
import { PROFILE_TYPES } from '@/app/data/model_constants';

export default function useProfile() {
  const store = useStore();
  const emitter = inject('emitter');

  const { current_client, current_profile, current_profile_true, sidebar_type } = mapGetters([
    'current_client',
    'current_profile',
    'current_profile_true',
    'sidebar_type',
  ]);

  const { FETCH_CLIENT, FETCH_FEATURE_TOGGLES, FETCH_PROJECTS, FETCH_UNITS, FETCH_USER_PROFILE, INIT_DOCUMENTS_PREFS } =
    mapActions([
      'FETCH_CLIENT',
      'FETCH_FEATURE_TOGGLES',
      'FETCH_PROJECTS',
      'FETCH_UNITS',
      'FETCH_USER_PROFILE',
      'INIT_DOCUMENTS_PREFS',
    ]);

  const initialized = ref(false);
  const projects = ref({});

  let fetch_client_at = null;
  let fetch_feature_toggles_at = null;
  let fetch_profile_at = null;

  // direct read, not mapGetters: role-scoped getter, absent until initStore registers it
  const billings = computed(() => store.getters.billings);

  const billing_current_client = computed(() => {
    if (current_profile.value.type === PROFILE_TYPES.SUPERVISOR) return null;
    return _.find(billings.value, ['attributes.client_id', current_client.value.id]);
  });

  const favicon_url = computed(() =>
    _.includes([PROFILE_TYPES.SUPERVISOR], current_profile.value.type)
      ? null
      : current_client.value.attributes.picture_favicon_url,
  );

  async function fetchClient() {
    // locking mechanism to avoid concurrent fetches, with auto-release after 60s (on error)
    if (differenceInSeconds(fetch_client_at, new Date()) > 60) fetch_client_at = null;
    if (initialized.value && fetch_client_at) return;
    fetch_client_at = new Date();
    await FETCH_CLIENT({ id: current_profile.value.attributes.client_id });
    fetch_client_at = null;
  }

  async function fetchFeatureToggles() {
    if (differenceInSeconds(fetch_feature_toggles_at, new Date()) > 60) fetch_feature_toggles_at = null;
    if (initialized.value && fetch_feature_toggles_at) return;
    fetch_feature_toggles_at = new Date();
    await FETCH_FEATURE_TOGGLES({});
    fetch_feature_toggles_at = null;
  }

  async function fetchProfile() {
    // locking mechanism to avoid concurrent fetches, with fail-safe auto-release after 60s
    if (differenceInSeconds(fetch_profile_at, new Date()) > 60) fetch_profile_at = null;
    if (initialized.value && fetch_profile_at) return;
    fetch_profile_at = new Date();
    await FETCH_USER_PROFILE({});
    fetch_profile_at = null;
  }

  async function fetchProjects() {
    // vuex getters are not guaranteed to be initialized, thus saving via a local ref instead
    const projects_fetched = await FETCH_PROJECTS({ skip_customer: false, followed: true });
    projects.value = _.keyBy(projects_fetched, 'id');
  }

  async function initProfile() {
    await Promise.all([
      current_profile.value.type !== PROFILE_TYPES.SUPERVISOR ? fetchClient() : null,
      current_profile.value.type !== PROFILE_TYPES.SUPERVISOR ? fetchProjects() : null,
      current_profile.value.type === PROFILE_TYPES.CUSTOMER ? FETCH_UNITS() : null,
    ]);
    if (current_profile.value.type !== PROFILE_TYPES.SUPERVISOR) {
      INIT_DOCUMENTS_PREFS({});
    }
    emitter.$emit('initTitle');
    emitter.$emit('initFavicon', favicon_url.value);
    if (_.get(current_profile.value, 'type') === PROFILE_TYPES.CUSTOMER) {
      emitter.$emit('initPWA');
    }
    emitter.$emit('initIntercom');
    emitter.$emit('initRecording');
    initialized.value = true;
  }

  return {
    billing_current_client,
    current_client,
    current_profile,
    current_profile_true,
    fetchClient,
    fetchFeatureToggles,
    fetchProfile,
    initialized,
    initProfile,
    projects,
    sidebar_type,
  };
}
